import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './Contact.css';

// ─── EmailJS Configuration ───────────────────────────────────────────────────
// 1. Sign up free at https://www.emailjs.com
// 2. Add a Gmail/Outlook service → copy Service ID below
// 3. Create a template → copy Template ID below
//    In the template, map these variables:
//    {{from_name}}, {{from_email}}, {{phone}}, {{company}},
//    {{service}}, {{message}} → To email: contactus@csrfinancialsolutions.com
// 4. Copy your Public Key from Account → API Keys


const EMAILJS_SERVICE_ID  = 'service_tpg4yfs';   // e.g. 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'template_5kgie92';  // e.g. 'template_xyz789'
const EMAILJS_PUBLIC_KEY  = 'nGmKy-9XM0Qfp1uuB';   // e.g. 'abcDEFghiJKL'



const SERVICES = [
  'BPO / Call Center Services',
  'Knowledge Process Outsourcing (KPO)',
  'HR Outsourcing',
  'Debt Recovery & Collections',
  'AI-Augmented Collections',
  'US Process Outsourcing',
  'General Enquiry',
];

const CONTACT_INFO = [
  {
    icon: '📍',
    label: 'Address',
    value: 'No 67, 1st floor, 5th Cross, 3rd Main Chamarajpet, Bangalore, Karnataka, India — 560018',
    link: null,
  },
  {
    icon: '📞',
    label: 'Phone',
    value: '08026601556',
    link: 'tel:+918026601556',
  },
  {
    icon: '✉️',
    label: 'Email',
    value: 'contactus@csrfinancialsolutions.com',
    link: 'mailto:contactus@csrfinancialsolutions.com',
  },
  {
    icon: '🕐',
    label: 'Business Hours',
    value: 'Monday – Saturday: 9:00 AM – 7:00 PM IST',
    link: null,
  },
  {
    icon: '🌐',
    label: 'International',
    value: 'US & UK enquiries welcome',
    link: null,
  },
];

export default function Contact() {
  const formRef = useRef();
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '',
    phone: '', company: '', service: '', message: '',
  });

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.firstName.trim())  e.firstName = 'First name is required';
    if (!form.lastName.trim())   e.lastName  = 'Last name is required';
    if (!form.email.trim())      e.email     = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.service)           e.service   = 'Please select a service';
    if (!form.message.trim())    e.message   = 'Message is required';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // ── Submit → EmailJS ────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus('sending');
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: `${form.firstName} ${form.lastName}`,
          from_email: form.email,
          phone:      form.phone   || 'Not provided',
          company:    form.company || 'Not provided',
          service:    form.service,
          message:    form.message,
          to_email:   'contactus@csrfinancialsolutions.com',
          reply_to:   form.email,
        },
        EMAILJS_PUBLIC_KEY
      );
      setStatus('success');
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  };

  const resetForm = () => {
    setForm({ firstName:'', lastName:'', email:'', phone:'', company:'', service:'', message:'' });
    setErrors({});
    setStatus('idle');
  };

  return (
    <div className="contact-page">

      {/* ── Hero ── */}
      <section className="contact-hero">
        <div className="contact-hero-grid" aria-hidden="true" />
        <div className="container contact-hero-inner">
          <div className="page-tag">Contact Us</div>
          <h1 className="page-h">
            Let's Start a{' '}
            <span className="hero-italic">Conversation</span>
          </h1>
          <p className="page-sub">
            Ready to explore how CSR's Financial Solutions can reduce costs and
            scale your operations? Our team responds within one business day.
          </p>
          <div className="hero-badges">
            <span className="hero-badge">⚡ Fast Response</span>
            <span className="hero-badge">🔒 Confidential</span>
            <span className="hero-badge">🌍 Global Enquiries Welcome</span>
          </div>
        </div>
      </section>

      {/* ── Body ── */}
      <section className="contact-body">
        <div className="container">
          <div className="contact-grid">

            {/* ── Left — Info ── */}
            <aside className="contact-aside">
              <div className="eyebrow">Get in Touch</div>
              <h2 className="section-h">
                We'd Love to <span className="gold">Hear From You</span>
              </h2>
              <p className="aside-lead">
                Whether you're looking to outsource operations, scale collections,
                or explore AI-driven solutions — we're here to help.
              </p>

              <div className="ci-list">
                {CONTACT_INFO.map(({ icon, label, value, link }) => (
                  <div className="ci-item" key={label}>
                    <div className="ci-icon">{icon}</div>
                    <div className="ci-text">
                      <div className="ci-label">{label}</div>
                      {link
                        ? <a className="ci-value ci-link" href={link}>{value}</a>
                        : <div className="ci-value">{value}</div>
                      }
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp CTA */}
              <a
                className="wa-cta"
                href="https://wa.me/919008328891?text=Hello%2C%20I%27d%20like%20to%20know%20more%20about%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="wa-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </span>
                Chat on WhatsApp
              </a>
            </aside>

            {/* ── Right — Form ── */}
            <div className="form-wrap">

              {/* SUCCESS */}
              {status === 'success' && (
                <div className="form-state">
                  <div className="state-icon state-success">✓</div>
                  <h3>Message Sent!</h3>
                  <p>
                    Thank you for reaching out. Our team will contact you at{' '}
                    <strong>{form.email}</strong> within one business day.
                  </p>
                  <button className="form-submit" onClick={resetForm}>
                    Send Another Message
                  </button>
                </div>
              )}

              {/* ERROR */}
              {status === 'error' && (
                <div className="form-state">
                  <div className="state-icon state-error">!</div>
                  <h3>Something Went Wrong</h3>
                  <p>
                    We couldn't send your message. Please email us directly at{' '}
                    <a href="mailto:contactus@csrfinancialsolutions.com">
                      contactus@csrfinancialsolutions.com
                    </a>
                  </p>
                  <button className="form-submit" onClick={() => setStatus('idle')}>
                    Try Again
                  </button>
                </div>
              )}

              {/* FORM */}
              {(status === 'idle' || status === 'sending') && (
                <>
                  <div className="form-header">
                    <h3>Send Us a Message</h3>
                    <p>All fields marked <span className="req">*</span> are required.</p>
                  </div>

                  <form ref={formRef} onSubmit={handleSubmit} noValidate>

                    {/* Name row */}
                    <div className="form-row">
                      <div className={`form-group ${errors.firstName ? 'has-error' : ''}`}>
                        <label htmlFor="firstName">First Name <span className="req">*</span></label>
                        <input
                          id="firstName" name="firstName" type="text"
                          placeholder="Rahul" value={form.firstName}
                          onChange={handleChange} disabled={status === 'sending'}
                        />
                        {errors.firstName && <span className="err-msg">{errors.firstName}</span>}
                      </div>
                      <div className={`form-group ${errors.lastName ? 'has-error' : ''}`}>
                        <label htmlFor="lastName">Last Name <span className="req">*</span></label>
                        <input
                          id="lastName" name="lastName" type="text"
                          placeholder="Sharma" value={form.lastName}
                          onChange={handleChange} disabled={status === 'sending'}
                        />
                        {errors.lastName && <span className="err-msg">{errors.lastName}</span>}
                      </div>
                    </div>

                    {/* Email + Phone row */}
                    <div className="form-row">
                      <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
                        <label htmlFor="email">Email <span className="req">*</span></label>
                        <input
                          id="email" name="email" type="email"
                          placeholder="rahul@company.com" value={form.email}
                          onChange={handleChange} disabled={status === 'sending'}
                        />
                        {errors.email && <span className="err-msg">{errors.email}</span>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">Phone</label>
                        <input
                          id="phone" name="phone" type="tel"
                          placeholder="+91 98765 43210" value={form.phone}
                          onChange={handleChange} disabled={status === 'sending'}
                        />
                      </div>
                    </div>

                    {/* Company */}
                    <div className="form-group">
                      <label htmlFor="company">Company / Organisation</label>
                      <input
                        id="company" name="company" type="text"
                        placeholder="Your Company Name" value={form.company}
                        onChange={handleChange} disabled={status === 'sending'}
                      />
                    </div>

                    {/* Service */}
                    <div className={`form-group ${errors.service ? 'has-error' : ''}`}>
                      <label htmlFor="service">Service Interest <span className="req">*</span></label>
                      <select
                        id="service" name="service"
                        value={form.service} onChange={handleChange}
                        disabled={status === 'sending'}
                      >
                        <option value="">Select a service…</option>
                        {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {errors.service && <span className="err-msg">{errors.service}</span>}
                    </div>

                    {/* Message */}
                    <div className={`form-group ${errors.message ? 'has-error' : ''}`}>
                      <label htmlFor="message">Message <span className="req">*</span></label>
                      <textarea
                        id="message" name="message"
                        placeholder="Tell us about your requirements and how we can help…"
                        value={form.message} onChange={handleChange}
                        disabled={status === 'sending'}
                      />
                      {errors.message && <span className="err-msg">{errors.message}</span>}
                    </div>

                    <button
                      type="submit"
                      className={`form-submit ${status === 'sending' ? 'sending' : ''}`}
                      disabled={status === 'sending'}
                    >
                      {status === 'sending' ? (
                        <><span className="spinner" /> Sending…</>
                      ) : (
                        <>✉ Send Message</>
                      )}
                    </button>

                    <p className="form-note">
                      By submitting, you agree to be contacted by CSR Financial Solutions.
                      Your data is kept confidential and never shared.
                    </p>

                  </form>
                </>
              )}

            </div>
          </div>
        </div>
      </section>

    </div>
  );
}